import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import {
  BACKGROUND_DIM_MAX,
  BACKGROUND_DIM_MIN,
  THEMES,
  type BackgroundStyle,
  type ColorMode,
  type ThemeId,
} from '@recipes/contracts';
import { Card, CardContent, CardHeader, CardTitle, Label, Slider, cn } from '@recipes/ui';
import { toast } from '@/components/feedback/toaster';
import { ImageUploader, type UploaderImage } from '@/components/media/image-uploader';
import { useFeature } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import {
  chooseAppearance,
  currentAppearance,
  type AppearanceChoice,
} from '@/lib/appearance/appearance';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { forgetOwnPhoto } from '@/lib/appearance/own-photo';
import { useAppearance, useChooseAppearance, useEnabledThemes } from './use-appearance';

const MODES: Array<{ mode: ColorMode; icon: typeof Sun }> = [
  { mode: 'LIGHT', icon: Sun },
  { mode: 'DARK', icon: Moon },
  { mode: 'SYSTEM', icon: Monitor },
];

/**
 * Profile → Appearance (docs/partial/appearance-and-themes-spec.md 5.1): light / dark / system,
 * the themes as preview tiles, the background. A tap applies at once – there is no save button;
 * a toast offers Undo. Every group is a radio group with visible names, usable by keyboard.
 */
export function AppearanceCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, setUser } = useAuth();
  const appearance = useAppearance();
  const choose = useChooseAppearance();
  const themes = useEnabledThemes();
  const customAllowed = useFeature('custom_backgrounds');
  const [photo, setPhoto] = useState<UploaderImage[]>([]);
  const [dim, setDim] = useState(appearance.backgroundDim);
  useEffect(() => setDim(appearance.backgroundDim), [appearance.backgroundDim]);

  const change = (patch: AppearanceChoice, message: string) => {
    const before = currentAppearance();
    const undo: AppearanceChoice = Object.fromEntries(
      Object.keys(patch).map((key) => [key, before[key as keyof typeof before]]),
    );
    void choose(patch);
    toast.show({
      title: message,
      action: { label: t('common.undo'), onAction: () => void choose(undo) },
    });
  };

  /** The own photo lives in the profile only: upload → profile → the URL comes back signed. */
  const savePhoto = async (assetId: string | null) => {
    try {
      const profile = await api.users.updateProfile(
        assetId
          ? { backgroundAssetId: assetId, backgroundStyle: 'CUSTOM' }
          : { backgroundAssetId: null },
      );
      queryClient.setQueryData(queryKeys.profile, profile);
      if (!assetId) await forgetOwnPhoto();
      if (user)
        setUser({
          ...user,
          backgroundUrl: profile.backgroundUrl,
          backgroundStyle: profile.backgroundStyle,
        });
      // On the device at once; the profile has it already, so nothing is left to save.
      chooseAppearance({ backgroundStyle: profile.backgroundStyle }, true);
      toast.success(
        assetId ? t('appearance.background.photoSaved') : t('appearance.background.photoRemoved'),
      );
    } catch (error) {
      toastError(error);
    }
  };

  const hasPhoto = Boolean(user?.backgroundUrl) || photo.length > 0;
  const styles: BackgroundStyle[] = ['NONE', 'PATTERN', 'IMAGE'];
  if (customAllowed && user) styles.push('CUSTOM');

  return (
    <Card data-testid="appearance-card">
      <CardHeader>
        <CardTitle>{t('appearance.title')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('appearance.description')}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <RadioRow label={t('appearance.mode.title')}>
          {MODES.map(({ mode, icon: Icon }) => (
            <Choice
              key={mode}
              checked={appearance.colorMode === mode}
              onSelect={() =>
                change(
                  { colorMode: mode },
                  t('appearance.mode.changed', { mode: t(`appearance.mode.${mode}`) }),
                )
              }
              testId={`color-mode-${mode.toLowerCase()}`}
              className="flex-1 flex-col gap-1 py-3"
            >
              <Icon className="size-5" aria-hidden />
              {t(`appearance.mode.${mode}`)}
            </Choice>
          ))}
        </RadioRow>

        <RadioRow label={t('appearance.themes.title')} wrap>
          {themes.map((id) => (
            <Choice
              key={id}
              checked={appearance.theme === id}
              onSelect={() =>
                change(
                  { theme: id },
                  t('appearance.themes.changed', { theme: t(THEMES[id].nameKey) }),
                )
              }
              testId={`theme-option-${id}`}
              className="w-40 flex-col items-stretch gap-2 p-2"
            >
              <ThemePreview id={id} />
              <span className="flex items-center justify-between gap-1 px-1 text-left">
                <span>
                  <span className="block font-medium">{t(THEMES[id].nameKey)}</span>
                  <span className="block text-xs font-normal text-muted-foreground">
                    {t(`appearance.themes.${id}.hint`)}
                  </span>
                </span>
                {appearance.theme === id && <Check className="size-4 shrink-0" aria-hidden />}
              </span>
            </Choice>
          ))}
        </RadioRow>

        <RadioRow label={t('appearance.background.title')} wrap>
          {styles.map((style) => (
            <Choice
              key={style}
              checked={appearance.backgroundStyle === style}
              disabled={style === 'CUSTOM' && !hasPhoto}
              onSelect={() =>
                change(
                  { backgroundStyle: style },
                  t('appearance.background.changed', {
                    style: t(`appearance.background.${style}`),
                  }),
                )
              }
              testId={`background-option-${style}`}
              className="px-4 py-2"
            >
              {t(`appearance.background.${style}`)}
            </Choice>
          ))}
        </RadioRow>
        <p className="-mt-4 text-xs text-muted-foreground">{t('appearance.background.hint')}</p>

        {customAllowed && user && (
          <div className="space-y-3" data-testid="background-own-photo">
            <Label className="block">{t('appearance.background.ownPhoto')}</Label>
            {user.backgroundUrl && photo.length === 0 ? (
              <div className="flex items-center gap-3">
                <img
                  src={user.backgroundUrl}
                  alt=""
                  className="h-16 w-28 rounded-md border object-cover"
                />
                <button
                  type="button"
                  className="text-sm underline underline-offset-2"
                  onClick={() => void savePhoto(null)}
                  data-testid="background-photo-remove"
                >
                  {t('appearance.background.removePhoto')}
                </button>
              </div>
            ) : (
              <ImageUploader
                purpose="THEME_BACKGROUND"
                value={photo}
                onChange={(images) => {
                  setPhoto(images);
                  const ready = images[0];
                  if (ready?.assetId) void savePhoto(ready.assetId).then(() => setPhoto([]));
                }}
                allowAltText={false}
                label={t('appearance.background.uploadPhoto')}
                data-testid="background-photo-upload"
              />
            )}
            {appearance.backgroundStyle === 'CUSTOM' && (
              <div>
                <Label htmlFor="background-dim" className="mb-2 block">
                  {t('appearance.background.dim', { percent: dim })}
                </Label>
                <Slider
                  id="background-dim"
                  min={BACKGROUND_DIM_MIN}
                  max={BACKGROUND_DIM_MAX}
                  step={5}
                  value={[dim]}
                  onValueChange={([value]) => setDim(value ?? dim)}
                  onValueCommit={([value]) => void choose({ backgroundDim: value ?? dim })}
                  aria-label={t('appearance.background.dimLabel')}
                  data-testid="background-dim"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('appearance.background.dimHint')}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RadioRow({
  label,
  wrap,
  children,
}: {
  label: string;
  wrap?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div role="radiogroup" aria-label={label}>
      <p className="mb-2 text-sm font-medium" aria-hidden>
        {label}
      </p>
      <div className={cn('flex gap-2', wrap && 'flex-wrap')}>{children}</div>
    </div>
  );
}

function Choice({
  checked,
  disabled,
  onSelect,
  testId,
  className,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  testId: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      // One tab stop per group; the arrow keys move inside it.
      tabIndex={checked ? 0 : -1}
      onClick={() => !checked && onSelect()}
      onKeyDown={(event) => {
        const step =
          event.key === 'ArrowRight' || event.key === 'ArrowDown'
            ? 1
            : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
              ? -1
              : 0;
        if (!step) return;
        event.preventDefault();
        const options = [
          ...(event.currentTarget
            .closest('[role="radiogroup"]')
            ?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? []),
        ];
        const next =
          options[(options.indexOf(event.currentTarget) + step + options.length) % options.length];
        next?.focus();
        next?.click();
      }}
      data-testid={testId}
      className={cn(
        'inline-flex min-h-[44px] items-center justify-center rounded-lg border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
        checked
          ? 'border-primary bg-accent text-accent-foreground ring-1 ring-primary'
          : 'bg-card hover:bg-muted',
        className,
      )}
    >
      {children}
    </button>
  );
}

/** A miniature of a card with a button in the theme's colours – from the registry, not the CSS. */
function ThemePreview({ id }: { id: ThemeId }) {
  const { background, card, primary, accents } = THEMES[id].preview;
  return (
    <span
      className="block rounded-md border p-2"
      style={{ background }}
      aria-hidden
      data-testid={`theme-preview-${id}`}
    >
      <span className="block rounded p-2 shadow-sm" style={{ background: card }}>
        <span className="mb-1.5 flex gap-1">
          <span className="h-2 w-8 rounded-full" style={{ background: accents[0] }} />
          <span className="h-2 w-5 rounded-full" style={{ background: accents[1] }} />
        </span>
        <span className="block h-4 w-14 rounded" style={{ background: primary }} />
      </span>
    </span>
  );
}
